import { api } from './cliente'
import type {
    ActualizarPromocion,
    ArqueoResumen,
    Autorizador,
    Caja,
    Categoria,
    Cliente,
    Comprobante,
    Configuracion,
    CrearPromocion,
    CreditoPendiente,
    DesgloseEfectivo,
    Devolucion,
    EstadoCuenta,
    Existencia,
    InventarioValorizado,
    Kardex,
    MedioPago,
    MovimientoCaja,
    MovimientoCredito,
    MovimientoInventario,
    OrdenProductos,
    Pagina,
    Producto,
    ProductoVendido,
    Promocion,
    PromocionAplicada,
    CatalogoPos,
    Region,
    ReporteCierre,
    ReporteVentas,
    Respaldo,
    ResultadoCobro,
    ResultadoResolucion,
    Rol,
    SesionCaja,
    SesionIniciada,
    Sucursal,
    Traspaso,
    TipoMovimientoInventario,
    TipoPromocion,
    AlcancesDisponibles,
    UnidadMedida,
    UsuarioAdmin,
    UsuarioAutenticado,
    Venta,
} from './tipos'

export const autenticacion = {
    entrar: (nombreUsuario: string, contrasena: string) =>
        api.post<SesionIniciada>('/auth/login', { nombreUsuario, contrasena }),

    salir: (tokenRefresco: string | null) =>
        api.post<void>('/auth/logout', { tokenRefresco }),

    yo: () => api.get<UsuarioAutenticado>('/auth/me'),

    cambiarContrasena: (contrasenaActual: string, contrasenaNueva: string) =>
        api.post<void>('/auth/cambiar-contrasena', { contrasenaActual, contrasenaNueva }),

    /**
     * Pasa a operar en otro local. Devuelve una sesion nueva: los tokens
     * anteriores quedan revocados, asi que hay que reemplazarlos.
     */
    cambiarSucursal: (sucursalId: number) =>
        api.post<SesionIniciada>('/auth/cambiar-sucursal', { sucursalId }),

    /** Usuarios que pueden autorizar una operacion concreta. */
    autorizadores: (permiso: string) =>
        api.get<Autorizador[]>('/usuarios/autorizadores', { permiso }),
}

export const configuracion = {
    obtener: () => api.get<Configuracion>('/configuracion'),

    actualizar: (cuerpo: {
        razonSocial: string
        rut?: string
        direccion?: string
        telefono?: string
        correo?: string
        redondeoEfectivo: number
        ivaPorcentaje: number
        preciosIncluyenIva: boolean
        prefijoDocumentoVenta: string
        mensajeTicket?: string
        zonaHoraria: string
    }) => api.put<Configuracion>('/configuracion', cuerpo),

    /** Regiones que el servidor sabe configurar, con su preset completo. */
    regiones: () => api.get<Region[]>('/configuracion/regiones'),

    /**
     * Aplica el preset de una región. Va aparte de `actualizar` porque no es
     * editar un campo: cambia moneda, decimales, redondeo, impuesto y zona de
     * una vez. Con ventas ya registradas el servidor lo rechaza con
     * `region_con_ventas` hasta que se reintente con `confirmar`.
     */
    aplicarRegion: (region: string, confirmar = false) =>
        api.put<Configuracion>('/configuracion/region', { region, confirmar }),

    /**
     * Zonas que ofrece el servidor. La lista viene de la API y no del
     * navegador para que solo se pueda elegir una que el servidor sepa
     * resolver: si difieren, los reportes se agrupan por un dia y las
     * pantallas muestran otro.
     */
    zonasHorarias: () =>
        api.get<{ id: string; desfaseActual: string }[]>('/configuracion/zonas-horarias'),
}

export const respaldos = {
    listar: () => api.get<Respaldo[]>('/respaldos'),

    crear: () => api.post<Respaldo>('/respaldos'),
}

export const caja = {
    listarCajas: () => api.get<Caja[]>('/cajas'),

    sesionActiva: () => api.get<SesionCaja | undefined>('/sesiones-caja/activa'),

    abrir: (cajaId: number, montoInicial: number, observacion?: string) =>
        api.post<SesionCaja>('/sesiones-caja', { cajaId, montoInicial, observacion }),

    obtenerSesion: (sesionCajaId: number) =>
        api.get<SesionCaja>(`/sesiones-caja/${sesionCajaId}`),

    movimientos: (sesionCajaId: number) =>
        api.get<MovimientoCaja[]>(`/sesiones-caja/${sesionCajaId}/movimientos`),

    registrarMovimiento: (
        sesionCajaId: number,
        cuerpo: {
            tipo: string
            importe: number
            motivo: string
            referencia?: string
            autorizadoPorUsuarioId: number
        },
    ) => api.post<MovimientoCaja>(`/sesiones-caja/${sesionCajaId}/movimientos`, cuerpo),

    /**
     * Registra el conteo del efectivo y, en la misma llamada, lo que imprimió
     * el lector de tarjetas. Va junto a propósito: declarar en dos pasos haría
     * el segundo sabiendo ya el resultado del primero, y el cierre dejaría de
     * ser ciego.
     */
    registrarConteo: (
        sesionCajaId: number,
        efectivoContado: number,
        totalesDeclarados: { medioPagoId: number; importe: number }[] = [],
    ) =>
        api.post<SesionCaja>(`/sesiones-caja/${sesionCajaId}/conteo`, {
            efectivoContado,
            totalesDeclarados,
        }),

    esperado: (sesionCajaId: number) =>
        api.get<DesgloseEfectivo>(`/sesiones-caja/${sesionCajaId}/esperado`),

    cerrar: (sesionCajaId: number, observacion?: string) =>
        api.post<SesionCaja>(`/sesiones-caja/${sesionCajaId}/cerrar`, { observacion }),

    reporteCierre: (sesionCajaId: number) =>
        api.get<ReporteCierre>(`/sesiones-caja/${sesionCajaId}/reporte-cierre`),

    historial: (pagina = 1) =>
        api.get<Pagina<SesionCaja>>('/sesiones-caja', { pagina, tamanio: 20 }),
}

export const ventas = {
    abrir: (clienteId?: number) => api.post<Venta>('/ventas', { clienteId }),

    obtener: (ventaId: number) => api.get<Venta>(`/ventas/${ventaId}`),

    resolver: (codigo: string, senal?: AbortSignal) =>
        api.get<ResultadoResolucion>('/productos/resolver', { codigo }, senal),

    resolverPorId: (productoId: number) =>
        api.get<ResultadoResolucion>(`/productos/${productoId}/resolver`),

    agregarLinea: (ventaId: number, cuerpo: { codigo?: string; productoId?: number; cantidad?: number }) =>
        api.post<Venta>(`/ventas/${ventaId}/lineas`, cuerpo),

    cambiarCantidad: (ventaId: number, ventaDetalleId: number, cantidad: number) =>
        api.put<Venta>(`/ventas/${ventaId}/lineas/${ventaDetalleId}`, { cantidad }),

    quitarLinea: (ventaId: number, ventaDetalleId: number) =>
        api.delete<Venta>(`/ventas/${ventaId}/lineas/${ventaDetalleId}`),

    aplicarDescuento: (
        ventaId: number,
        cuerpo: { ventaDetalleId?: number; tipo: string; valor: number; motivo: string },
    ) => api.post<Venta>(`/ventas/${ventaId}/descuentos`, cuerpo),

    quitarDescuento: (ventaId: number, descuentoVentaId: number) =>
        api.delete<Venta>(`/ventas/${ventaId}/descuentos/${descuentoVentaId}`),

    asignarCliente: (ventaId: number, clienteId: number | null) =>
        api.put<Venta>(`/ventas/${ventaId}/cliente`, { clienteId }),

    completar: (
        ventaId: number,
        cuerpo: {
            pagos: { medioPagoId: number; importe: number; referencia?: string }[]
            saldoACredito: number
            montoRecibidoEfectivo?: number
        },
    ) => api.post<ResultadoCobro>(`/ventas/${ventaId}/completar`, cuerpo),

    descartar: (ventaId: number) => api.delete<void>(`/ventas/${ventaId}`),

    comprobante: (ventaId: number) => api.get<Comprobante>(`/ventas/${ventaId}/comprobante`),

    /**
     * Medios de pago habilitados, con su identificador real.
     *
     * Los da el servidor y no una lista escrita aca: los identificadores los
     * asigna la base al sembrar, y asumir "efectivo es el 1" registra los
     * cobros contra el medio equivocado en cuanto la base se crea en otro
     * orden. Eso no da ningun error; solo un cierre que no cuadra.
     */
    mediosPago: () => api.get<MedioPago[]>('/medios-pago'),

    /** Busca ventas ya cobradas: es el punto de partida de una devolucion. */
    buscar: (parametros: { texto?: string; estado?: string; pagina?: number }) =>
        api.get<Pagina<Venta>>('/ventas', { tamanio: 15, ...parametros }),
}

export const devoluciones = {
    listar: (parametros: { estado?: string; pagina?: number }) =>
        api.get<Pagina<Devolucion>>('/devoluciones', { tamanio: 20, ...parametros }),

    obtener: (devolucionId: number) => api.get<Devolucion>(`/devoluciones/${devolucionId}`),

    crear: (ventaId: number, motivo: string) =>
        api.post<Devolucion>('/devoluciones', { ventaId, motivo }),

    agregarLinea: (
        devolucionId: number,
        cuerpo: { ventaDetalleId: number; cantidad: number; condicionProducto?: string },
    ) => api.post<Devolucion>(`/devoluciones/${devolucionId}/lineas`, cuerpo),

    quitarLinea: (devolucionId: number, detalleId: number) =>
        api.delete<Devolucion>(`/devoluciones/${devolucionId}/lineas/${detalleId}`),

    confirmar: (
        devolucionId: number,
        cuerpo: {
            reembolsos: { medioPagoId: number; importe: number; referencia?: string }[]
            autorizadaPorUsuarioId: number
        },
    ) => api.post<Devolucion>(`/devoluciones/${devolucionId}/confirmar`, cuerpo),

    anular: (devolucionId: number, motivo: string) =>
        api.post<Devolucion>(`/devoluciones/${devolucionId}/anular`, { motivo }),
}

export const catalogo = {
    buscarProductos: (texto: string, senal?: AbortSignal) =>
        api.get<Pagina<Producto>>('/productos', { texto, activo: true, tamanio: 12 }, senal),

    productos: (parametros: {
        texto?: string
        categoriaId?: number
        activo?: boolean
        pagina?: number
        tamanio?: number
        orden?: OrdenProductos
    }) => api.get<Pagina<Producto>>('/productos', { tamanio: 25, ...parametros }),

    /**
     * Grilla del POS. Pide mas por pagina que el catalogo y por defecto
     * ordena por lo que mas se vende, que es lo que el cajero tiene que
     * alcanzar sin buscar.
     */
    /**
     * Catálogo completo para tener en memoria. Se pide una vez por turno; con
     * unos miles de productos pesa un par de megabytes.
     */
    catalogoPos: () => api.get<CatalogoPos>('/productos/catalogo-pos'),

    grillaPos: (
        parametros: { texto?: string; categoriaId?: number; orden?: OrdenProductos; pagina?: number },
        senal?: AbortSignal,
    ) =>
        api.get<Pagina<Producto>>(
            '/productos',
            { activo: true, tamanio: 48, orden: 'MasVendidos', ...parametros },
            senal,
        ),

    subirImagen: (productoId: number, archivo: File) =>
        api.subirArchivo<Producto>(`/productos/${productoId}/imagen`, archivo),

    quitarImagen: (productoId: number) =>
        api.delete<Producto>(`/productos/${productoId}/imagen`),

    producto: (productoId: number) => api.get<Producto>(`/productos/${productoId}`),

    crearProducto: (cuerpo: {
        sku: string
        nombre: string
        categoriaId: number
        unidadMedidaId: number
        tipo: string
        stockMinimo: number
        precio: number
        codigoBarra?: string
    }) => api.post<Producto>('/productos', cuerpo),

    actualizarProducto: (
        productoId: number,
        cuerpo: { nombre: string; descripcion?: string; categoriaId: number; stockMinimo: number; activo: boolean },
    ) => api.put<Producto>(`/productos/${productoId}`, cuerpo),

    cambiarPrecio: (productoId: number, precio: number, motivo: string) =>
        api.post<{ precio: number }>(`/productos/${productoId}/precios`, { precio, motivo }),

    agregarCodigoBarra: (productoId: number, codigo: string, principal: boolean) =>
        api.post<void>(`/productos/${productoId}/codigos-barra`, { codigo, principal }),

    categorias: (soloActivas = true) => api.get<Categoria[]>('/categorias', { soloActivas }),

    crearCategoria: (nombre: string, categoriaPadreId?: number) =>
        api.post<Categoria>('/categorias', { nombre, categoriaPadreId }),

    unidades: (soloActivas = true) => api.get<UnidadMedida[]>('/unidades-medida', { soloActivas }),
}

export const inventario = {
    existencias: (parametros: { texto?: string; soloBajoMinimo?: boolean; pagina?: number }) =>
        api.get<Pagina<Existencia>>('/inventario/existencias', { tamanio: 25, ...parametros }),

    kardex: (productoId: number, desde?: string, hasta?: string) =>
        api.get<Kardex>(`/inventario/kardex/${productoId}`, { desde, hasta }),

    movimientos: (parametros: { tipo?: string; estado?: string; pagina?: number }) =>
        api.get<Pagina<MovimientoInventario>>('/inventario/movimientos', { tamanio: 20, ...parametros }),

    crearMovimiento: (cuerpo: {
        tipo: TipoMovimientoInventario
        motivo?: string
        proveedorNombre?: string
        referenciaExterna?: string
    }) => api.post<MovimientoInventario>('/inventario/movimientos', cuerpo),

    agregarDetalle: (
        movimientoId: number,
        cuerpo: { productoId: number; cantidad: number; costoUnitario?: number },
    ) => api.post<MovimientoInventario>(`/inventario/movimientos/${movimientoId}/detalles`, cuerpo),

    quitarDetalle: (movimientoId: number, detalleId: number) =>
        api.delete<MovimientoInventario>(`/inventario/movimientos/${movimientoId}/detalles/${detalleId}`),

    confirmar: (movimientoId: number, autorizadoPorUsuarioId?: number) =>
        api.post<MovimientoInventario>(`/inventario/movimientos/${movimientoId}/confirmar`, {
            autorizadoPorUsuarioId,
        }),

    anular: (movimientoId: number, motivo: string) =>
        api.post<MovimientoInventario>(`/inventario/movimientos/${movimientoId}/anular`, { motivo }),
}

/**
 * Sobre que local se calcula un reporte. Sin nada, el del usuario; con
 * `sucursalId`, ese; con `consolidado`, todos sumados. Los dos ultimos exigen
 * el permiso `reporte.global` y el backend los rechaza sin el.
 */
export interface AlcanceReporte {
    sucursalId?: number
    consolidado?: boolean
}

/** Los mismos dos parametros, listos para la cadena de consulta. */
export function parametrosAlcance(alcance: AlcanceReporte = {}): Record<string, number | boolean | undefined> {
    return {
        sucursalId: alcance.consolidado ? undefined : alcance.sucursalId,
        consolidado: alcance.consolidado ? true : undefined,
    }
}

export const reportes = {
    alcances: () => api.get<AlcancesDisponibles>('/reportes/alcances'),

    ventas: (desde: string, hasta: string, alcance: AlcanceReporte = {}) =>
        api.get<ReporteVentas>('/reportes/ventas', { desde, hasta, ...parametrosAlcance(alcance) }),

    productosMasVendidos: (desde: string, hasta: string, porImporte = true, alcance: AlcanceReporte = {}) =>
        api.get<ProductoVendido[]>('/reportes/productos-mas-vendidos', {
            desde, hasta, porImporte, cantidad: 15, ...parametrosAlcance(alcance),
        }),

    inventarioValorizado: (alcance: AlcanceReporte = {}) =>
        api.get<InventarioValorizado>('/reportes/inventario-valorizado', parametrosAlcance(alcance)),

    creditoPendiente: () => api.get<CreditoPendiente[]>('/reportes/credito-pendiente'),

    arqueos: (desde: string, hasta: string, soloConDiferencia = false, alcance: AlcanceReporte = {}) =>
        api.get<ArqueoResumen[]>('/reportes/arqueos', {
            desde, hasta, soloConDiferencia, ...parametrosAlcance(alcance),
        }),

    promociones: (desde: string, hasta: string, alcance: AlcanceReporte = {}) =>
        api.get<PromocionAplicada[]>('/reportes/promociones', { desde, hasta, ...parametrosAlcance(alcance) }),
}

export const traspasos = {
    listar: (filtro: {
        direccion?: 'Todos' | 'Salientes' | 'Entrantes'
        estado?: string
        pagina?: number
    } = {}) =>
        api.get<Pagina<Traspaso>>('/traspasos', { tamanio: 25, ...filtro }),

    pendientes: () => api.get<number>('/traspasos/pendientes'),

    obtener: (traspasoId: number) => api.get<Traspaso>(`/traspasos/${traspasoId}`),

    crear: (cuerpo: { sucursalDestinoId: number; observacion?: string }) =>
        api.post<Traspaso>('/traspasos', cuerpo),

    agregarLinea: (traspasoId: number, cuerpo: { productoId: number; cantidad: number; observacion?: string }) =>
        api.post<Traspaso>(`/traspasos/${traspasoId}/lineas`, cuerpo),

    quitarLinea: (traspasoId: number, detalleId: number) =>
        api.delete<Traspaso>(`/traspasos/${traspasoId}/lineas/${detalleId}`),

    enviar: (traspasoId: number) => api.post<Traspaso>(`/traspasos/${traspasoId}/enviar`, {}),

    recibir: (
        traspasoId: number,
        cuerpo: {
            lineas: { traspasoDetalleId: number; cantidadRecibida: number }[]
            motivoDiferencia?: string
        },
    ) => api.post<Traspaso>(`/traspasos/${traspasoId}/recibir`, cuerpo),

    anular: (traspasoId: number, motivo: string) =>
        api.post<Traspaso>(`/traspasos/${traspasoId}/anular`, { motivo }),
}

export const promociones = {
    listar: (filtro: { texto?: string; tipo?: TipoPromocion; soloVigentes?: boolean } = {}) =>
        api.get<Promocion[]>('/promociones', {
            texto: filtro.texto,
            tipo: filtro.tipo,
            soloVigentes: filtro.soloVigentes,
        }),

    obtener: (promocionId: number) => api.get<Promocion>(`/promociones/${promocionId}`),

    crear: (cuerpo: CrearPromocion) => api.post<Promocion>('/promociones', cuerpo),

    // El tipo y el alcance no se editan: para cambiarlos se crea otra promocion
    // y se desactiva esta, porque las ventas ya cobradas apuntan a esta fila.
    actualizar: (promocionId: number, cuerpo: ActualizarPromocion) =>
        api.put<Promocion>(`/promociones/${promocionId}`, cuerpo),
}

export const usuarios = {
    listar: (texto?: string) => api.get<Pagina<UsuarioAdmin>>('/usuarios', { texto, tamanio: 50 }),

    crear: (cuerpo: {
        nombreUsuario: string
        nombreCompleto: string
        correo?: string
        contrasena: string
        roles: string[]
        sucursalId?: number | null
    }) => api.post<UsuarioAdmin>('/usuarios', cuerpo),

    actualizar: (
        usuarioId: number,
        cuerpo: {
            nombreCompleto: string
            correo?: string
            activo: boolean
            sucursalId?: number | null
        },
    ) => api.put<UsuarioAdmin>(`/usuarios/${usuarioId}`, cuerpo),

    asignarRoles: (usuarioId: number, roles: string[]) =>
        api.put<UsuarioAdmin>(`/usuarios/${usuarioId}/roles`, { roles }),

    restablecerContrasena: (usuarioId: number, contrasenaNueva: string) =>
        api.post<void>(`/usuarios/${usuarioId}/restablecer-contrasena`, { contrasenaNueva }),

    roles: () => api.get<Rol[]>('/roles'),
}

export const clientes = {
    buscar: (texto: string, senal?: AbortSignal) =>
        api.get<Pagina<Cliente>>('/clientes', { texto, activo: true, tamanio: 10 }, senal),

    listar: (parametros: { texto?: string; soloConDeuda?: boolean; pagina?: number }) =>
        api.get<Pagina<Cliente>>('/clientes', { tamanio: 25, ...parametros }),

    obtener: (clienteId: number) => api.get<Cliente>(`/clientes/${clienteId}`),

    crear: (cuerpo: { rut?: string; nombres: string; apellidos?: string; telefono?: string; direccion?: string }) =>
        api.post<Cliente>('/clientes', cuerpo),

    actualizar: (
        clienteId: number,
        cuerpo: { nombres: string; apellidos?: string; telefono?: string; direccion?: string; activo: boolean },
    ) => api.put<Cliente>(`/clientes/${clienteId}`, cuerpo),

    estadoCuenta: (clienteId: number) => api.get<EstadoCuenta>(`/clientes/${clienteId}/estado-cuenta`),

    registrarAbono: (
        clienteId: number,
        cuerpo: { importe: number; medioPagoId: number; referencia?: string; observacion?: string },
    ) => api.post<MovimientoCredito>(`/clientes/${clienteId}/abonos`, cuerpo),
}

export const sucursales = {
    listar: (soloActivas = true) => api.get<Sucursal[]>('/sucursales', { soloActivas }),

    crear: (cuerpo: { codigo: string; nombre: string; direccion?: string; cierreCiego: boolean }) =>
        api.post<Sucursal>('/sucursales', cuerpo),

    actualizar: (
        sucursalId: number,
        cuerpo: { nombre: string; direccion?: string; cierreCiego: boolean; activo: boolean },
    ) => api.put<Sucursal>(`/sucursales/${sucursalId}`, cuerpo),
}
